<template>
    <div class="tab-content">
        <div class="table-container-compact">
            <table class="table-compact">
                <thead>
                    <tr>
                        <th class="text-center">배출계수 코드</th>
                        <th class="text-center">배출계수 유형</th>
                        <th class="text-center">연료</th>
                        <th class="text-center">단위</th>
                        <th class="text-center">배출계수</th>
                        <th class="text-center">상태</th>
                        <th class="text-center">출처</th>
                        <th class="text-center"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="loading">
                        <td colspan="8" class="text-center"><i class="fas fa-spinner fa-spin"></i> 로딩 중</td>
                    </tr>
                    <tr v-else-if="filteredFactors.length === 0">
                        <td colspan="8" class="text-center empty-state">
                            <i class="fas fa-database"></i>
                            <p>배출계수가 없습니다</p>
                        </td>
                    </tr>
                    <tr
                        v-else
                        v-for="factor in paginatedFactors"
                        :key="factor.factorIdx"
                        class="clickable-row"
                        :class="{ 'inactive-row': factor.draftStat === 'INACTIVE' }"
                        @click="onRowClick(factor, $event)"
                    >
                        <td class="text-center">
                            <strong class="factor-code-cell">{{ factor.factorName || factor.factorCode }}</strong>
                            <span v-if="factor.factorName && factor.factorCode" class="factor-code-sub">{{
                                factor.factorCode
                            }}</span>
                        </td>
                        <td class="text-center">{{ getFactorTypeLabel(factor.factorType) }}</td>
                        <td class="text-center">{{ getFuelTypeLabel(factor.fuelType) }}</td>
                        <td class="text-center">{{ factor.unit }}</td>
                        <td class="text-center">{{ factor.currentValue ?? factor.factor ?? "-" }}</td>
                        <td class="text-center">
                            {{ formatDraftStatus(factor.draftStat) }}
                        </td>
                        <td class="text-center">{{ getSourceLabel(factor.source) }}</td>
                        <td>
                            <div class="action-buttons text-center">
                                <IconButton
                                    icon="fas fa-history"
                                    title="배출계수 버전 관리"
                                    @click="$emit('view-history', factor)"
                                />
                                <IconButton icon="fas fa-edit" title="수정" @click="$emit('edit', factor)" />
                                <IconButton
                                    icon="fas fa-trash"
                                    variant="delete"
                                    title="삭제"
                                    @click="$emit('delete', factor)"
                                />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <TablePagination
            v-if="!loading && filteredFactors.length > 0"
            :total-items="filteredFactors.length"
            :current-page="currentPage"
            :page-size="itemsPerPage"
            @update:currentPage="$emit('update:currentPage', $event)"
            @update:pageSize="$emit('update:itemsPerPage', $event)"
        />
    </div>
</template>

<script>
import CarbonCodes from "../../../codes";
import IconButton from "../../common/IconButton.vue";
import TablePagination from "../../common/TablePagination.vue";

export default {
    name: "FactorManagement",
    components: { IconButton, TablePagination },
    props: {
        factors: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        filterFuelType: {
            type: String,
            default: "",
        },
        filterDraftStat: {
            type: String,
            default: "",
        },
        searchKeyword: {
            type: String,
            default: "",
        },
        currentPage: {
            type: Number,
            default: 1,
        },
        itemsPerPage: {
            type: Number,
            default: 10,
        },
    },
    emits: [
        "view-detail",
        "view-history",
        "edit",
        "delete",
        "update:currentPage",
        "update:itemsPerPage",
        "update:total-filtered",
    ],
    computed: {
        getSourceLabel() {
            return (source) => CarbonCodes.factorSource.getLabel(source);
        },
        filteredFactors() {
            const filtered = this.factors.filter((factor) => {
                // 연료 유형 필터
                const matchFuelType = !this.filterFuelType || factor.fuelType === this.filterFuelType;

                // 상태 필터 (draftStat)
                const matchDraftStat = !this.filterDraftStat || factor.draftStat === this.filterDraftStat;

                // 검색 (factorCode 중심)
                const matchSearch =
                    !this.searchKeyword ||
                    (factor.factorCode && factor.factorCode.toLowerCase().includes(this.searchKeyword.toLowerCase())) ||
                    (factor.fuelType && factor.fuelType.toLowerCase().includes(this.searchKeyword.toLowerCase())) ||
                    (factor.source && factor.source.toLowerCase().includes(this.searchKeyword.toLowerCase()));

                return matchFuelType && matchDraftStat && matchSearch;
            });
            // 최신순 정렬 (factorIdx DESC) - 신규 추가 항목이 1페이지 상단에 보이도록
            return filtered.sort((a, b) => (b.factorIdx || 0) - (a.factorIdx || 0));
        },
        paginatedFactors() {
            const start = (this.currentPage - 1) * this.itemsPerPage;
            const end = start + this.itemsPerPage;
            return this.filteredFactors.slice(start, end);
        },
    },
    watch: {
        filteredFactors: {
            handler(factors) {
                this.$emit("update:total-filtered", factors.length);
            },
            immediate: true,
        },
    },
    methods: {
        onRowClick(factor, event) {
            if (event.target.closest(".action-buttons")) return;
            this.$emit("view-detail", factor);
        },
        formatDraftStatus(status) {
            return CarbonCodes.draftStat.getLabel(status);
        },
        getFactorTypeLabel(value) {
            return CarbonCodes.factorType.getLabel(value);
        },
        getFuelTypeLabel(value) {
            return CarbonCodes.fuelType.getLabel(value);
        },
    },
};
</script>

<style scoped>
.tab-content {
    flex: 1;
    min-height: 0;
}

.factor-code-cell {
    font-weight: 600;
}

.factor-code-sub {
    display: block;
    font-size: 11px;
    font-weight: normal;
    color: #6c757d;
    margin-top: 2px;
}
</style>
